import { memo, useCallback } from 'react';

import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import {
  ScaffolderFieldError,
  ScaffolderInputHeader,
} from '../../../components';
import * as doc from '../infoDoc';
import { useStyles } from '../styles';
import { Project } from '../types';

export interface ProjectPickerProps {
  onChange: (value: string) => void;
  projects: Project[];
  formData: string;
  loading: boolean;
  rawErrors: string[];
  disabled?: boolean;
}

const ProjectPickerInner = (props: ProjectPickerProps) => {
  const { onChange, formData, projects, loading, rawErrors, disabled } = props;
  const { classes } = useStyles();

  const handleProjectSelect = useCallback(
    (_: unknown, value: Project | null) => {
      onChange(value?.name || '');
    },
    [onChange],
  );

  const selectedProject = projects?.find(e => e.name === formData) || null;
  const hasError = Boolean(rawErrors?.length > 0) && !formData;

  return (
    <Box className={classes.inputContainer}>
      <ScaffolderInputHeader
        title="Project Details"
        description="Select the project you want to associate the application with."
        infoDoc={doc.PROJECT_INFO}
      />
      <Autocomplete
        size="small"
        value={selectedProject}
        loading={loading}
        onChange={handleProjectSelect}
        options={projects}
        disabled={disabled}
        getOptionLabel={option => option.title || option.name}
        isOptionEqualToValue={(option, value) => option.name === value.name}
        renderInput={params => (
          <TextField
            {...params}
            label="Project Name"
            margin="none"
            variant="outlined"
            required
            error={hasError}
            helperText={
              hasError ? (
                <ScaffolderFieldError message="Project is required" />
              ) : undefined
            }
          />
        )}
        renderOption={({ key, ...optionProps }, option) => (
          <li key={key} {...optionProps}>
            <Box>
              <Typography variant="body1" noWrap>
                {option.title}
              </Typography>
            </Box>
          </li>
        )}
        noOptionsText="No projects found"
        loadingText="Loading projects..."
      />
    </Box>
  );
};

export const ProjectPicker = memo(ProjectPickerInner);
